// Global visibility helpers for contract-fixture athletes.
//
// We keep the "Test Player" athlete (`0e378572-d49c-4bd2-8a92-6a166f84620f`)
// in the database — the backend test suite pins demo logins
// (player@pfaphub.com / parent@pfaphub.com) to this athlete id. But for
// Admin / Staff browsing UIs we hide it so the demo athlete lists feel clean.
//
// Strategy: we install a response interceptor on axios AND wrap window.fetch
// so every response body returned by any athlete-list endpoint is filtered
// for the current role (Admin / Staff).

import axios from 'axios';

export const CONTRACT_FIXTURE_ATHLETE_IDS = new Set([
  '0e378572-d49c-4bd2-8a92-6a166f84620f', // Test Player
]);

const HIDE_ROLES = new Set(['Admin', 'Staff']);

/**
 * Admin / Staff should never browse Test Player's full historical dataset.
 * Use this guard on athlete-scoped views that could land on Test Player
 * (direct URL, bookmarks, search). Returns true when the CURRENT role is
 * Admin / Staff AND the athlete being viewed is a contract fixture.
 * Player / Parent roles always return false — they keep the full dataset.
 */
export const shouldLimitForContractFixture = (athleteId) => {
  if (!athleteId) return false;
  try {
    const role = localStorage.getItem('userRole');
    if (!HIDE_ROLES.has(role)) return false;
    return CONTRACT_FIXTURE_ATHLETE_IDS.has(athleteId);
  } catch {
    return false;
  }
};

const ATHLETE_LIST_URL_PATTERNS = [
  /\/api\/athletes(\?|$)/, // base list w/ optional query
  /\/api\/athletes\/cards-data/,
  /\/api\/athletes\/search/,
  /\/api\/athletes\/staff-list/, // staff list is users, but harmless
];

const isAthleteListUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  return ATHLETE_LIST_URL_PATTERNS.some((p) => p.test(url));
};

const currentRole = () => {
  try {
    return localStorage.getItem('userRole') || null;
  } catch {
    return null;
  }
};

const shouldHide = () => HIDE_ROLES.has(currentRole());

// -----------------------------------------------------------------------------
// Shape-aware visibility registry.
//
// Explicit per-endpoint handlers. No recursion. No blanket string-match. Each
// handler knows the exact shape of its target endpoint and transforms only the
// fields it is certified to own. Aggregated metrics (counters / averages) are
// never modified — only the lists that contain athlete references are filtered.
// -----------------------------------------------------------------------------

// Shape primitives (pure functions).
const shapes = {
  // S1 — flat athlete-like: [{id, full_name, ...}]
  S1: (arr, ids) =>
    Array.isArray(arr) ? arr.filter((x) => x && !ids.has(x.id)) : arr,

  // S2 — flat FK row: [{athlete_id, ...}]
  S2: (arr, ids) =>
    Array.isArray(arr) ? arr.filter((x) => x && !ids.has(x.athlete_id)) : arr,

  // S3 — nested athlete block: [{athlete: {id}, ...}]
  S3: (arr, ids) =>
    Array.isArray(arr) ? arr.filter((x) => !(x?.athlete && ids.has(x.athlete.id))) : arr,

  // S5 — bare id array: [id, id, ...]
  S5: (arr, ids) =>
    Array.isArray(arr) ? arr.filter((id) => !ids.has(id)) : arr,

  // S6 — dict keyed by id: {id: obj, ...}
  S6: (obj, ids) => {
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
    const out = {};
    for (const k of Object.keys(obj)) if (!ids.has(k)) out[k] = obj[k];
    return out;
  },
};

const registry = [
  // ---- athletes collection surfaces (kept for continuity) -----------------
  {
    name: 'athletes_list',
    match: /\/api\/athletes(\?|$)/,
    apply: (data, ids) => shapes.S1(data, ids),
  },
  {
    name: 'athletes_cards_data',
    match: /\/api\/athletes\/cards-data/,
    apply: (data, ids) => shapes.S6(data, ids),
  },
  {
    name: 'athletes_search',
    match: /\/api\/athletes\/search/,
    apply: (data, ids) => shapes.S1(data, ids),
  },
  {
    name: 'athletes_staff_list',
    match: /\/api\/athletes\/staff-list/,
    apply: (data, ids) => shapes.S1(data, ids),
  },

  // ---- Partial rollout (Option A) ----------------------------------------
  {
    name: 'injury_active_board',
    match: /\/api\/injury-recovery\/active-injuries/,
    apply: (data, ids) => shapes.S3(data, ids),
  },
  {
    name: 'dashboard_coach_today',
    match: /\/api\/dashboard\/coach-today/,
    apply: (data, ids) => {
      if (!data || typeof data !== 'object') return data;
      const next = { ...data };
      if (Array.isArray(next.attention)) next.attention = shapes.S2(next.attention, ids);
      if (Array.isArray(next.athlete_rows)) next.athlete_rows = shapes.S1(next.athlete_rows, ids);
      if (next.availability && typeof next.availability === 'object') {
        const av = { ...next.availability };
        if (Array.isArray(av.injured_athlete_ids)) av.injured_athlete_ids = shapes.S5(av.injured_athlete_ids, ids);
        if (Array.isArray(av.no_wellness_athlete_ids)) av.no_wellness_athlete_ids = shapes.S5(av.no_wellness_athlete_ids, ids);
        next.availability = av;
      }
      // Counters (total_active, available, injured, no_wellness_today) are
      // intentionally left untouched — server-computed aggregates stay honest.
      return next;
    },
  },
  {
    name: 'dashboard_kpis',
    match: /\/api\/dashboard\/kpis/,
    apply: (data, ids) => {
      if (!data || typeof data !== 'object') return data;
      const next = { ...data };
      if (Array.isArray(next.recent_matches)) next.recent_matches = shapes.S2(next.recent_matches, ids);
      if (Array.isArray(next.alerts)) next.alerts = shapes.S2(next.alerts, ids);
      if (Array.isArray(next.active_flags)) next.active_flags = shapes.S2(next.active_flags, ids);
      if (Array.isArray(next.injured_athletes)) next.injured_athletes = shapes.S1(next.injured_athletes, ids);
      // total_athletes, fit_count, injured_count, avg_pdp_progress,
      // athletes_with_pdp, attendance_pct, squad_counts, active_flags_count
      // are NEVER modified — they are server-computed aggregates.
      return next;
    },
  },
];

// Dev-mode guardrail: surface athlete-ish URLs that escaped the registry.
// Noise-suppressed to one warning per URL per session.
const SUSPICIOUS_URL = /\/api\/(dashboard|analytics|injury-|training-|wellness-|athletes|match-reports|pdp|documents|assessments|career)/;
const DEV_WARN_SEEN = new Set();
const devWarnUnregistered = (url) => {
  try {
    const isDev = typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production';
    if (!isDev) return;
  } catch { return; }
  if (!url || DEV_WARN_SEEN.has(url)) return;
  DEV_WARN_SEEN.add(url);
  // eslint-disable-next-line no-console
  console.warn(`[visibility] no registry match for ${url} — if this response contains athlete data, add a handler.`);
};

const findRegistryEntry = (url) => {
  if (!url || typeof url !== 'string') return null;
  return registry.find((r) => r.match.test(url)) || null;
};

const filterPayload = (url, data) => {
  if (!shouldHide()) return data;
  const entry = findRegistryEntry(url);
  if (!entry) {
    if (SUSPICIOUS_URL.test(url || '')) devWarnUnregistered(url);
    return data;
  }
  try {
    return entry.apply(data, CONTRACT_FIXTURE_ATHLETE_IDS);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[visibility] handler ${entry.name} threw`, e);
    return data;
  }
};

let installed = false;

export const installVisibilityInterceptors = () => {
  if (installed) return;
  installed = true;

  // 1) Axios — response interceptor (runs the registry on every URL).
  axios.interceptors.response.use(
    (response) => {
      try {
        const url = response?.config?.url || '';
        response.data = filterPayload(url, response.data);
      } catch {
        /* no-op */
      }
      return response;
    },
    (error) => Promise.reject(error),
  );

  // 2) window.fetch — transparent wrapper (same rule set, same registry).
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const response = await originalFetch(input, init);
      try {
        if (!shouldHide()) return response;
        const url = typeof input === 'string' ? input : input?.url;
        const entry = findRegistryEntry(url);
        if (!entry) {
          if (SUSPICIOUS_URL.test(url || '')) devWarnUnregistered(url);
          return response;
        }
        const cloned = response.clone();
        const ctype = cloned.headers.get('content-type') || '';
        if (!ctype.includes('application/json')) return response;
        const body = await cloned.json();
        const filtered = filterPayload(url, body);
        return new Response(JSON.stringify(filtered), {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      } catch {
        return response;
      }
    };
  }
};

/**
 * Limit array to N most-recent items, sorted by a date-like field.
 * Non-mutating. Safe on non-arrays.
 */
export const limitRecent = (arr, n = 5, dateField = null) => {
  if (!Array.isArray(arr)) return arr;
  const candidateFields = dateField
    ? [dateField]
    : ['created_at', 'updated_at', 'date', 'session_date', 'match_date', 'recorded_at', 'submitted_at'];
  const pickDate = (item) => {
    for (const f of candidateFields) {
      if (item && item[f]) return new Date(item[f]).getTime() || 0;
    }
    return 0;
  };
  return [...arr].sort((a, b) => pickDate(b) - pickDate(a)).slice(0, n);
};

// -----------------------------------------------------------------------------
// "Archive from Active View" — UI-only, localStorage-backed.
//
// This lets staff reduce clutter on the Active Injuries Board without ever
// touching the backend. Archived ids are filtered from that board only —
// the underlying record and every other view (Athlete 360, history,
// analytics) remain untouched.
// -----------------------------------------------------------------------------

const INJURY_ARCHIVED_KEY = 'pfap.injury.archivedActive.v1';

const readArchived = () => {
  try {
    const raw = localStorage.getItem(INJURY_ARCHIVED_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const writeArchived = (set) => {
  try {
    localStorage.setItem(INJURY_ARCHIVED_KEY, JSON.stringify([...set]));
    window.dispatchEvent(new Event('pfap:injury-archive-change'));
  } catch {
    /* no-op */
  }
};

export const getArchivedInjuryIds = () => readArchived();

export const isInjuryArchived = (injuryId) => {
  if (!injuryId) return false;
  return readArchived().has(injuryId);
};

export const archiveInjuryFromActiveView = (injuryId) => {
  if (!injuryId) return;
  const set = readArchived();
  set.add(injuryId);
  writeArchived(set);
};

export const unarchiveInjuryFromActiveView = (injuryId) => {
  if (!injuryId) return;
  const set = readArchived();
  set.delete(injuryId);
  writeArchived(set);
};

export const clearAllArchivedInjuries = () => writeArchived(new Set());
